import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useTheme } from '../../../contexts';

const BackgroundContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 0;
  overflow: hidden;
  pointer-events: none;
`;

const ThermalCanvas = styled.canvas`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const NeonElement = styled(motion.div)`
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  filter: blur(${({ $blur }) => $blur}px);
  mix-blend-mode: screen;
  box-shadow: 0 0 30px ${({ $color }) => $color}60, inset 0 0 30px ${({ $color }) => $color}30;
  will-change: transform, opacity;
`;

const GridOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: 
    linear-gradient(${({ theme }) => theme.colors.primary.main}15 1px, transparent 1px),
    linear-gradient(90deg, ${({ theme }) => theme.colors.primary.main}15 1px, transparent 1px);
  background-size: 60px 60px;
  opacity: 0.7;
  animation: gridPulse 5s ease-in-out infinite;
  
  @keyframes gridPulse {
    0%, 100% { opacity: 0.5; }
    50% { opacity: 0.8; }
  }
`;

const ScanLine = styled(motion.div)`
  position: absolute;
  left: 0;
  width: 100%;
  height: 3px;
  background: linear-gradient(90deg, 
    transparent 0%, 
    ${({ theme }) => theme.colors.primary.main}80 30%,
    ${({ theme }) => theme.colors.secondary.main}80 70%,
    transparent 100%);
  box-shadow: 
    0 0 30px ${({ theme }) => theme.colors.primary.main}80,
    0 0 60px ${({ theme }) => theme.colors.primary.main}40;
`;

const Particle = styled(motion.div)`
  position: absolute;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  pointer-events: none;
  box-shadow: 0 0 8px ${({ $color }) => $color};
  will-change: transform, opacity;
`;

const ThermalBackground = ({ variant = 'default' }) => {
  const canvasRef = useRef(null);
  const requestRef = useRef(null);
  const [neonElements, setNeonElements] = useState([]);
  const [particles, setParticles] = useState([]);
  const [scanLineY, setScanLineY] = useState(0);
  const [scanLines, setScanLines] = useState([]);
  const { theme } = useTheme();

  // Generate neon elements based on page variant
  const generateNeonElements = useCallback((variant) => {
    const elements = [];
    const configs = {
      home: {
        count: 6,
        colors: [theme.colors.primary.main, theme.colors.secondary.main, theme.colors.thermal.warm, theme.colors.thermal.hot],
        sizeRange: [200, 500],
        blurRange: [40, 80],
      },
      about: {
        count: 5,
        colors: [theme.colors.primary.main, theme.colors.thermal.cool, theme.colors.thermal.cold, theme.colors.thermal.warm],
        sizeRange: [180, 450],
        blurRange: [35, 70],
      },
      products: {
        count: 7,
        colors: [theme.colors.secondary.main, theme.colors.thermal.hot, theme.colors.thermal.warm, theme.colors.primary.main],
        sizeRange: [250, 550],
        blurRange: [50, 100],
      },
      technology: {
        count: 8,
        colors: [theme.colors.primary.main, theme.colors.thermal.cool, theme.colors.thermal.warm, theme.colors.thermal.hot, theme.colors.secondary.main],
        sizeRange: [150, 400],
        blurRange: [30, 60],
      },
      team: {
        count: 5,
        colors: [theme.colors.primary.main, theme.colors.secondary.main, theme.colors.thermal.warm],
        sizeRange: [200, 480],
        blurRange: [40, 80],
      },
      contact: {
        count: 6,
        colors: [theme.colors.thermal.warm, theme.colors.thermal.hot, theme.colors.primary.main, theme.colors.secondary.main],
        sizeRange: [220, 520],
        blurRange: [45, 90],
      },
      default: {
        count: 5,
        colors: [theme.colors.primary.main, theme.colors.secondary.main, theme.colors.thermal.warm],
        sizeRange: [200, 450],
        blurRange: [40, 80],
      },
    };

    const config = configs[variant] || configs.default;

    for (let i = 0; i < config.count; i++) {
      elements.push({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: config.sizeRange[0] + Math.random() * (config.sizeRange[1] - config.sizeRange[0]),
        color: config.colors[Math.floor(Math.random() * config.colors.length)],
        blur: config.blurRange[0] + Math.random() * (config.blurRange[1] - config.blurRange[0]),
        opacity: 0.15 + Math.random() * 0.35,
        speed: 0.5 + Math.random() * 2,
        direction: Math.random() > 0.5 ? 1 : -1,
        orbitRadius: 20 + Math.random() * 50,
      });
    }

    return elements;
  }, [theme]);

  // Initialize neon elements
  useEffect(() => {
    setNeonElements(generateNeonElements(variant));
  }, [variant, generateNeonElements]);

  // Generate particles
  useEffect(() => {
    const particleCount = 50;
    const newParticles = [];
    const gradientColors = {
      home: [theme.colors.primary.main, theme.colors.thermal.warm],
      about: [theme.colors.thermal.cool, theme.colors.thermal.cold],
      products: [theme.colors.thermal.hot, theme.colors.secondary.main],
      technology: [theme.colors.primary.main, theme.colors.thermal.cool],
      team: [theme.colors.primary.main, theme.colors.secondary.main],
      contact: [theme.colors.thermal.warm, theme.colors.thermal.hot],
      default: [theme.colors.primary.main, theme.colors.secondary.main],
    };

    const colors = gradientColors[variant] || gradientColors.default;

    for (let i = 0; i < particleCount; i++) {
      newParticles.push({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: 2 + Math.random() * 4,
        color: colors[Math.floor(Math.random() * colors.length)],
        speedX: (Math.random() - 0.5) * 0.3,
        speedY: (Math.random() - 0.5) * 0.3,
        opacity: 0.3 + Math.random() * 0.5,
      });
    }

    setParticles(newParticles);
  }, [variant, theme]);

  // Initialize scan lines for technology page
  useEffect(() => {
    if (variant === 'technology') {
      const lines = [
        { id: 1, y: 0, speed: 2 },
        { id: 2, y: 0, speed: 1.5 },
        { id: 3, y: 0, speed: 1 },
      ];
      setScanLines(lines);
    } else {
      setScanLines([]);
    }
  }, [variant]);

  // Canvas render loop - optimized with aggressive throttling
  const lastRenderTimeRef = useRef(0);
  const RENDER_THROTTLE_MS = 33; // ~30 FPS - smoother feel but still efficient

  const render = useCallback((timestamp) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Throttle rendering to reduce CPU load
    if (timestamp - lastRenderTimeRef.current < RENDER_THROTTLE_MS) {
      requestRef.current = requestAnimationFrame(render);
      return;
    }
    lastRenderTimeRef.current = timestamp;

    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();

    // Only update canvas size if needed
    if (canvas.width !== rect.width || canvas.height !== rect.height) {
      canvas.width = rect.width;
      canvas.height = rect.height;
    }

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    requestRef.current = requestAnimationFrame(render);
  }, [neonElements, variant, theme]);

  // Animate scan lines - slowed down
  useEffect(() => {
    const animateScan = () => {
      setScanLines(prev => prev.map(line => ({
        ...line,
        y: (line.y + line.speed * 0.5) % window.innerHeight,
      })));
      setScanLineY(prev => (prev + 0.8) % window.innerHeight);
    };
    const interval = setInterval(animateScan, 33); // ~30 FPS for responsiveness
    return () => clearInterval(interval);
  }, []);

  // Start render loop
  useEffect(() => {
    render();
    return () => {
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
    };
  }, [render]);

  return (
    <BackgroundContainer>
      <ThermalCanvas ref={canvasRef} />

      {/* Animated neon elements - larger and more vibrant */}
      {neonElements.map(element => (
        <NeonElement
          key={element.id}
          style={{
            left: `${element.x}%`,
            top: `${element.y}%`,
            width: `${element.size}px`,
            height: `${element.size}px`,
            backgroundColor: element.color,
            opacity: element.opacity,
          }}
          $blur={element.blur}
          $color={element.color}
          animate={{
            x: [0, element.orbitRadius * element.direction * 0.7, 0, -element.orbitRadius * element.direction * 0.7, 0],
            y: [0, element.orbitRadius * element.direction * 0.7, 0, -element.orbitRadius * element.direction * 0.7, 0],
            scale: [1, 1.1, 1, 1.08, 1],
            opacity: [element.opacity, element.opacity * 1.15, element.opacity, element.opacity * 1.1, element.opacity],
          }}
          transition={{
            duration: 6 + element.speed * 1.5,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      ))}

      {/* Floating particles */}
      {particles.map(particle => (
        <Particle
          key={particle.id}
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: `${particle.size}px`,
            height: `${particle.size}px`,
            backgroundColor: particle.color,
            opacity: particle.opacity,
          }}
          $color={particle.color}
          animate={{
            x: particle.speedX * 80,
            y: particle.speedY * 80,
            opacity: [particle.opacity, particle.opacity * 0.6, particle.opacity],
          }}
          transition={{
            duration: 10 + Math.random() * 6,
            repeat: Infinity,
            ease: 'linear',
          }}
        />
      ))}

      {/* Multiple scan lines for technology page */}
      {variant === 'technology' && scanLines.map((line, index) => (
        <ScanLine
          key={line.id}
          style={{ top: line.y }}
          animate={{ opacity: [0.4, 0.8, 0.4] }}
          transition={{
            duration: 4 + index * 0.8,
            repeat: Infinity,
            delay: index * 0.5
          }}
        />
      ))}

      {/* Scan line for other pages */}
      {variant !== 'technology' && (
        <ScanLine
          style={{ top: scanLineY }}
          animate={{ opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 4, repeat: Infinity }}
        />
      )}

      {/* Enhanced grid overlay */}
      <GridOverlay />
    </BackgroundContainer>
  );
};

export default ThermalBackground;